import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { PeriodKey, PeriodSelection } from '../types'

const validPeriods: PeriodKey[] = ['today', '7d', '30d', 'custom']
const DEFAULT_CUSTOM_FROM = '2026-01-20'
const DEFAULT_CUSTOM_TO = '2026-02-03'

function isValidIsoDate(value: string | null) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export function usePeriodSelection() {
  const [searchParams, setSearchParams] = useSearchParams()
  const keyParam = searchParams.get('period') as PeriodKey | null
  const key = keyParam && validPeriods.includes(keyParam) ? keyParam : '30d'
  const rawFrom = searchParams.get('from')
  const rawTo = searchParams.get('to')
  const normalizedCustomRange = useMemo(() => {
    let from = isValidIsoDate(rawFrom) ? rawFrom! : DEFAULT_CUSTOM_FROM
    let to = isValidIsoDate(rawTo) ? rawTo! : DEFAULT_CUSTOM_TO
    if (from > to) [from, to] = [to, from]
    return { from, to }
  }, [rawFrom, rawTo])
  const from = key === 'custom' ? normalizedCustomRange.from : undefined
  const to = key === 'custom' ? normalizedCustomRange.to : undefined

  const selection = useMemo<PeriodSelection>(() => ({ key, from, to }), [from, key, to])

  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    let changed = false

    if (keyParam && !validPeriods.includes(keyParam)) {
      params.set('period', '30d')
      changed = true
    }

    if (key === 'custom') {
      if (rawFrom !== normalizedCustomRange.from) {
        params.set('from', normalizedCustomRange.from)
        changed = true
      }
      if (rawTo !== normalizedCustomRange.to) {
        params.set('to', normalizedCustomRange.to)
        changed = true
      }
    } else {
      if (rawFrom !== null) {
        params.delete('from')
        changed = true
      }
      if (rawTo !== null) {
        params.delete('to')
        changed = true
      }
    }

    if (changed) setSearchParams(params, { replace: true })
  }, [key, keyParam, normalizedCustomRange, rawFrom, rawTo, searchParams, setSearchParams])

  const setSelection = useCallback((next: PeriodSelection) => {
    const params = new URLSearchParams(searchParams)
    params.set('period', next.key)
    if (next.key === 'custom') {
      params.set('from', next.from ?? DEFAULT_CUSTOM_FROM)
      params.set('to', next.to ?? DEFAULT_CUSTOM_TO)
    } else {
      params.delete('from')
      params.delete('to')
    }
    setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams])

  return { selection, setSelection }
}
